import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * ProductSEO - Ürün detay sayfası için SEO optimizasyonu bileşeni
 * 
 * @param {Object} props
 * @param {Object} props.product - Ürün objesi
 */
const ProductSEO = ({ product }) => {
  if (!product) return null;

  // Görsel URL'lerini hazırla
  const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  const baseUrl = window.location.origin;
  
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return `${baseUrl}/assets/images/logo/logo.png`;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${imageApiUrl}${imageUrl}`;
  };

  const mainImage = getFullImageUrl(product.imageUrl);
  const additionalImages = product.additionalImages?.map(img => getFullImageUrl(img)) || [];
  const allImages = [mainImage, ...additionalImages];

  // Meta bilgiler
  const title = product.metaTitle || `${product.name} | Gümüş Pestil`;
  const description = product.metaDescription || product.shortDescription || product.description?.substring(0, 160);
  const currentUrl = window.location.href;
  const canonical = product.slug 
    ? `${baseUrl}/product-details/${product.slug}` 
    : currentUrl;

  // JSON-LD Product Schema
  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": allImages,
    "description": description,
    "sku": product.sku || undefined,
    "gtin": product.barcode || undefined,
    "brand": {
      "@type": "Brand",
      "name": "Gümüş Pestil"
    },
    "offers": {
      "@type": "Offer",
      "url": currentUrl,
      "priceCurrency": "TRY",
      "price": (product.discountedPrice || product.price).toFixed(2),
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "availability": product.isOutOfStock 
        ? "https://schema.org/OutOfStock" 
        : "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Gümüş Pestil"
      }
    }
  };

  // Rating varsa ekle
  if (product.rating > 0 && product.reviewCount > 0) {
    productSchema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.rating.toFixed(1),
      "reviewCount": product.reviewCount,
      "bestRating": "5",
      "worstRating": "1"
    };
  }

  // Ek özellikler
  if (product.weight > 0) {
    productSchema.weight = {
      "@type": "QuantitativeValue",
      "value": product.weight,
      "unitCode": "GRM"
    };
  }

  if (product.category) {
    productSchema.category = product.category.name;
  }

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Ana Sayfa",
        "item": baseUrl
      }
    ]
  };

  // Kategori varsa ekle
  if (product.category) {
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": product.category.name,
      "item": `${baseUrl}/shop?category=${product.categoryId}`
    });

    if (product.subCategory) {
      breadcrumbSchema.itemListElement.push({
        "@type": "ListItem",
        "position": 3,
        "name": product.subCategory.name,
        "item": `${baseUrl}/shop?category=${product.subCategoryId}`
      });
    }
  }

  // Ürün adını son sıraya ekle
  breadcrumbSchema.itemListElement.push({
    "@type": "ListItem",
    "position": breadcrumbSchema.itemListElement.length + 1,
    "name": product.name,
    "item": currentUrl
  });

  return (
    <Helmet>
      {/* Temel Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      
      {/* Keywords */}
      {product.tags && product.tags.length > 0 && (
        <meta name="keywords" content={product.tags.join(', ')} />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="product" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={mainImage} />
      <meta property="og:image:alt" content={product.name} />
      <meta property="og:site_name" content="Gümüş Pestil" />
      
      {/* Ürün özellikleri */}
      <meta property="product:price:amount" content={(product.discountedPrice || product.price).toFixed(2)} />
      <meta property="product:price:currency" content="TRY" />
      {product.category && <meta property="product:category" content={product.category.name} />}
      {!product.isOutOfStock && <meta property="product:availability" content="in stock" />}
      {product.isNew && <meta property="product:condition" content="new" />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={mainImage} />
      <meta name="twitter:image:alt" content={product.name} />

      {/* Mobile Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      
      {/* Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default ProductSEO;

